import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function HomeIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 11.5 12 5l8 6.5" />
      <path d="M6.5 10.5V19h11v-8.5" />
    </svg>
  );
}

export function PlacesIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  );
}

export function ForMeIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 5.6-7 10-7 10Z" />
    </svg>
  );
}

export function MapIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9 4.5 3.5 6.5v13L9 17.5 15 19.5l5.5-2v-13L15 6.5 9 4.5Z" />
      <path d="M9 4.5v13M15 6.5v13" />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3.5V7M16 3.5V7M3.5 10h17" />
    </svg>
  );
}

export function LighthouseIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3v3M8 5.5 6 4M16 5.5 18 4" />
      <path d="M10 6h4l1.5 14h-7L10 6Z" />
      <path d="M8.5 12h7" />
    </svg>
  );
}

export function MoreIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg {...base} width={18} height={18} {...props}>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...base} width={18} height={18} {...props}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 4 3.5 19h17L12 4Z" />
      <path d="M12 10v4M12 16.5v.5" />
    </svg>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M7 4.5h3l1.2 3L9.5 9.2a11 11 0 0 0 5.3 5.3l1.7-1.7 3 1.2v3A1.5 1.5 0 0 1 18 18.5 14 14 0 0 1 5.5 6 1.5 1.5 0 0 1 7 4.5Z" />
    </svg>
  );
}

export function HeartIcon({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <svg {...base} {...props} fill={filled ? 'currentColor' : 'none'}>
      <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 5.6-7 10-7 10Z" />
    </svg>
  );
}

export function GoogleMapsIcon(props: IconProps) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M12 21s6.5-5.2 6.5-10.2A6.5 6.5 0 0 0 5.5 10.8C5.5 15.8 12 21 12 21Z" fill="#EA4335" />
      <circle cx="12" cy="10.6" r="2.3" fill="white" />
    </svg>
  );
}

export function WazeIcon(props: IconProps) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path
        d="M12 4.5c4.4 0 7.5 3.2 7.5 7.2 0 2.4-1.2 4.4-2.6 5.6l.8 2.2-3-1.1c-.8.2-1.7.3-2.7.3-4.4 0-7.5-3.2-7.5-7.2S7.6 4.5 12 4.5Z"
        fill="#33CCFF"
      />
      <circle cx="9.4" cy="11.2" r="1.15" fill="#0B2545" />
      <circle cx="14.6" cy="11.2" r="1.15" fill="#0B2545" />
      <path d="M8.8 14.1c1 .9 2.1 1.3 3.2 1.3s2.2-.4 3.2-1.3" stroke="#0B2545" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function AppleMapsIcon(props: IconProps) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="#64D2FF" />
      <path d="M8 16.5 12 6.5l4 10" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9.4 13.4h5.2" stroke="white" strokeWidth="1.8" />
    </svg>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="8" />
      <path d="M4 12h16M12 4c2.4 2.4 3.6 5 3.6 8s-1.2 5.6-3.6 8c-2.4-2.4-3.6-5-3.6-8s1.2-5.6 3.6-8Z" />
    </svg>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4.5v2M12 17.5v2M4.5 12h2M17.5 12h2M6.4 6.4l1.4 1.4M16.2 16.2l1.4 1.4M17.6 6.4l-1.4 1.4M7.8 16.2 6.4 17.6" />
    </svg>
  );
}

export function EventsIcon(props: IconProps) {
  return <CalendarIcon {...props} />;
}

export function EmergencyIcon(props: IconProps) {
  return <AlertIcon {...props} />;
}

export function ExploreIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="8" />
      <path d="m10 14 1.2-3.8L15 9l-1.2 3.8L10 14Z" />
    </svg>
  );
}

export function FlightsIcon(props: IconProps) {
  return (
    <svg {...base} {...props} fill="currentColor" stroke="none" aria-hidden>
      {/* Nose-right airplane. Distinct from the calendar grid at 26px. */}
      <path d="M3 20.25 21.25 12 3 3.75v6.2L14.4 12 3 14.05Z" />
    </svg>
  );
}

export function MedicalIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9 4.5h6v4h4v6h-4v5H9v-5H5v-6h4v-4Z" />
    </svg>
  );
}

export function CheckIcon({ checked, ...props }: IconProps & { checked?: boolean }) {
  return (
    <svg {...base} width={16} height={16} {...props}>
      <rect x="3.5" y="3.5" width="13" height="13" rx="3" />
      {checked && <path d="m7 12 2.2 2.2 5-5.5" />}
    </svg>
  );
}

export function SpeakerIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4.5 9.5v5h3.2L12.5 19V5L7.7 9.5H4.5Z" />
      <path d="M16 9.2a3.6 3.6 0 0 1 0 5.6" />
      <path d="M18.4 7a6.2 6.2 0 0 1 0 10" />
    </svg>
  );
}
